import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Button, Table } from 'antd';
import { Download, FilePlus2 } from 'lucide-react';

import { useBranches, useReportData, useReports } from '../../hooks';
import {
  BranchSelect,
  Card,
  DataTable,
  DateRangeFilter,
  EmptyState,
  PageHeader,
  SearchInput,
} from '../../components';
import { DonutChart, HBarChart, TrendChart } from '../../components/Charts';
import { useI18n } from '../../i18n';
import { useTheme } from '../../theme';
import { ROUTE_URLS } from '../../routes';
import { exportCsv, formatMoney, formatNumber, getPresetRange } from '../../utils';

const UnitsCard = ({ data }) => {
  const { t } = useI18n();
  const { palette } = useTheme();
  return (
    <Card title={t('charts.topProducts')} subtitle={t('charts.topProductsSub')}>
      {data.length ? (
        <HBarChart
          data={data.slice(0, 5)}
          valueName={t('charts.units')}
          valueFormatter={v => `${formatNumber(v)} ${t('common.pcs')}`}
          color={palette.chart[1]}
        />
      ) : (
        <EmptyState compact title={t('states.emptyPeriod')} />
      )}
    </Card>
  );
};

export const Reports = () => {
  const { id } = useParams();
  const { isAdmin } = useSelector(state => state.authReducer);
  const { t } = useI18n();
  const { palette } = useTheme();
  const [selectedBranch, setSelectedBranch] = useState(id ? +id : 0);
  const [period, setPeriod] = useState(() => ({ preset: 'last7', range: getPresetRange('last7') }));
  const [search, setSearch] = useState('');

  const params = useMemo(
    () => ({ branch_id: selectedBranch, start_date: period.range[0], end_date: period.range[1] }),
    [selectedBranch, period],
  );

  const { data: branches, isLoading: isLoadingBranches } = useBranches();
  const { data, isLoading: isLoadingReports, isError, refetch } = useReports(params, {
    keepPreviousData: true,
  });

  const { columns, reportDates, data: rows, totals, lineData, barData, pieData } = useReportData(data);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? rows.filter(row => row.productName.toLowerCase().includes(q)) : rows;
  }, [rows, search]);

  const shareData = useMemo(() => {
    const head = pieData.slice(0, 6);
    const rest = pieData.slice(6).reduce((acc, item) => acc + item.value, 0);
    return rest > 0 ? [...head, { name: t('charts.others'), value: rest }] : head;
  }, [pieData, t]);

  const selectedBranchName = useMemo(
    () => branches?.find(branch => branch.id === selectedBranch)?.name ?? t('filters.allBranches'),
    [branches, selectedBranch, t],
  );

  const loading = isLoadingReports || !data;

  const summary = () =>
    totals && filteredRows.length > 0 && !search ? (
      <Table.Summary fixed='bottom'>
        <Table.Summary.Row>
          <Table.Summary.Cell index={0}>{t('reports.total')}</Table.Summary.Cell>
          {[...reportDates.map(date => totals[date]), { quantity: totals.totalQuantity, profit: totals.totalProfit }]
            .flatMap(cell => (isAdmin ? [cell.quantity, cell.profit] : [cell.quantity]))
            .map((value, i) => (
              <Table.Summary.Cell index={i + 1} key={i} align='right'>
                {formatNumber(value)}
              </Table.Summary.Cell>
            ))}
        </Table.Summary.Row>
      </Table.Summary>
    ) : null;

  const exportReport = () => {
    const header = [t('reports.productName')];
    reportDates.forEach(date => {
      header.push(`${date} · ${t('reports.quantity')}`);
      if (isAdmin) header.push(`${date} · ${t('reports.profit')}`);
    });
    header.push(`${t('reports.overall')} · ${t('reports.quantity')}`);
    if (isAdmin) header.push(`${t('reports.overall')} · ${t('reports.profit')}`);

    const body = rows.map(row => {
      const line = [row.productName];
      reportDates.forEach(date => {
        line.push(row[date].quantity);
        if (isAdmin) line.push(row[date].profit);
      });
      line.push(row.totalQuantity);
      if (isAdmin) line.push(row.totalProfit);
      return line;
    });
    exportCsv(`${t('reports.exportName')}-${period.range[0]}_${period.range[1]}`, header, body);
  };

  return (
    <>
      <PageHeader
        title={selectedBranch ? t('reports.titleFor', { branch: selectedBranchName }) : t('reports.title')}
        subtitle={t('reports.subtitle')}
        actions={
          <>
            <Button icon={<Download size={15} />} onClick={exportReport} disabled={!rows.length}>
              {t('common.exportCsv')}
            </Button>
            <Link to={ROUTE_URLS.CREATE_REPORT}>
              <Button type='primary' icon={<FilePlus2 size={15} />}>
                {t('nav.createReport')}
              </Button>
            </Link>
          </>
        }
      />

      <div className='wm-filter-bar'>
        <DateRangeFilter value={period} onChange={setPeriod} />
        <BranchSelect
          branches={branches}
          value={selectedBranch}
          onChange={setSelectedBranch}
          loading={isLoadingBranches}
        />
        <SearchInput value={search} onChange={setSearch} placeholder={t('products.searchPlaceholder')} />
      </div>

      <DataTable
        isLoading={loading}
        isError={isError}
        onRetry={refetch}
        columns={columns}
        dataSource={filteredRows}
        bordered
        summary={summary}
        emptyVariant={search ? 'search' : undefined}
        emptyTitle={search ? undefined : t('states.emptyPeriod')}
        emptyText={search ? undefined : t('states.emptyHint')}
        className='mb-16'
      />

      {!loading && rows.length > 0 && (
        <>
          <div className={`wm-grid ${isAdmin ? 'wm-grid--main-side' : 'wm-grid--2'}`}>
            {isAdmin ? (
              <Card title={t('charts.dailyProfit')}>
                {lineData.length < 2 ? (
                  <EmptyState compact title={t('states.emptyHint')} />
                ) : (
                  <TrendChart
                    data={lineData}
                    showPrevious={false}
                    valueFormatter={formatMoney}
                    currentLabel={t('charts.profit')}
                    color={palette.chart[2]}
                    height={300}
                  />
                )}
              </Card>
            ) : (
              <UnitsCard data={pieData} />
            )}
            <Card title={t('charts.productShare')} subtitle={t('charts.productShareSub')}>
              {shareData.length ? (
                <DonutChart data={shareData} centerLabel={t('charts.totalUnits')} />
              ) : (
                <EmptyState compact title={t('states.emptyPeriod')} />
              )}
            </Card>
          </div>
          {isAdmin && (
            <div className='wm-grid wm-grid--2'>
              <Card title={t('charts.productProfit')}>
                <HBarChart
                  data={barData}
                  valueName={t('charts.profit')}
                  valueFormatter={formatMoney}
                  color={palette.chart[0]}
                />
              </Card>
              <UnitsCard data={pieData} />
            </div>
          )}
        </>
      )}
    </>
  );
};
